import { useSyncExternalStore } from 'react';

/**
 * Per-device display preferences for DM folders (Telegram-style). These are
 * pure UI choices — where the folder tab strip sits and whether each chat row
 * shows its folder name(s) — so they live in localStorage, not on the server.
 * Reactive via useSyncExternalStore so toggling in the settings modal updates
 * the sidebar live.
 */

export type FolderTabPlacement = 'top' | 'left';

export interface DmFolderPrefs {
  /** Show folder-name tags next to each chat row in the DM list. */
  showTags: boolean;
  /** Where the folder tab strip sits relative to the chat list. */
  tabPlacement: FolderTabPlacement;
}

const STORAGE_KEY = 'bunker.dmFolderPrefs.v1';
const DEFAULTS: DmFolderPrefs = { showTags: false, tabPlacement: 'top' };

function load(): DmFolderPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<DmFolderPrefs>;
    return {
      showTags: parsed.showTags ?? DEFAULTS.showTags,
      tabPlacement: parsed.tabPlacement === 'left' ? 'left' : 'top',
    };
  } catch {
    return { ...DEFAULTS };
  }
}

function save(prefs: DmFolderPrefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // private mode — degrade to in-memory for this session.
  }
}

const listeners = new Set<() => void>();
let cache: DmFolderPrefs | null = null;

function getSnapshot(): DmFolderPrefs {
  if (!cache) cache = load();
  return cache;
}

function emit() {
  for (const fn of listeners) fn();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function update(patch: Partial<DmFolderPrefs>) {
  cache = { ...getSnapshot(), ...patch };
  save(cache);
  emit();
}

export function setShowTags(value: boolean): void {
  update({ showTags: value });
}

export function setTabPlacement(value: FolderTabPlacement): void {
  update({ tabPlacement: value });
}

/** Reactive hook — re-renders subscribers when any folder pref changes. */
export function useDmFolderPrefs(): DmFolderPrefs {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Evict the in-memory cache. Exported for unit tests only. */
export function _resetDmFolderPrefsForTest(): void {
  cache = null;
}
